import { InternalAxiosRequestConfig, AxiosError, AxiosInstance } from "axios";

import axiosInstanceNamek from "@/service/Axios/axios.instance.namek";
import { axiosInstanceLegacy } from "@/service/Axios/axios.instance.legacy";
import { axiosInstanceOrion } from "@/service/Axios";

import {
  clearTokens,
  getLegacyTokens,
  getNamekToken,
  hasLegacyTokens,
  saveLegacyTokens,
  saveNamekToken,
} from "../../store/tokens.store";
import { postLegacyRefreshToken } from "../legacy/postLegacyRefreshToken";
import { isOrionAuthenticateRequest, postOrionAuthenticate } from "../orionv2";

type AvoiderLoop = {
  _isRecentlySolvedError: boolean;
  _timeoutHandler: NodeJS.Timeout | undefined;
  setRecentlySolvedError: () => void;
  checkLoop: () => void;
};

const avoiderLoop: AvoiderLoop = {
  _isRecentlySolvedError: false,
  _timeoutHandler: undefined,
  setRecentlySolvedError() {
    this._isRecentlySolvedError = true;
    clearTimeout(this._timeoutHandler);
    this._timeoutHandler = setTimeout(() => {
      this._isRecentlySolvedError = false;
    }, 2000);
  },
  checkLoop() {
    if (this._isRecentlySolvedError) {
      clearTimeout(this._timeoutHandler);
      throw new Error("Internal error, avoid loop refreshToken");
    }
  },
};

const handleAuthErrors = async (
  error: AxiosError,
  instance: AxiosInstance,
  redirectToLogout: () => void,
) => {
  const handleFailRefresh = () => {
    clearTokens();
    return redirectToLogout();
  };

  if (
    error.response?.status === 401 &&
    !error.config?.url?.endsWith("/login") &&
    !isOrionAuthenticateRequest(error.config?.url)
  ) {
    const config = error.config;

    if (!hasLegacyTokens()) {
      handleFailRefresh();
    }

    avoiderLoop.checkLoop();

    const refreshTokenLegacy = getLegacyTokens().refreshToken;

    try {
      const legacyData = await postLegacyRefreshToken(refreshTokenLegacy);
      saveLegacyTokens({ authToken: legacyData.AuthToken, refreshToken: legacyData.RefreshToken });

      if (!legacyData) {
        return handleFailRefresh();
      }

      const namekData = await postOrionAuthenticate(legacyData.AuthToken);
      if (!namekData) {
        return handleFailRefresh();
      }
      saveNamekToken(namekData.token);
      avoiderLoop.setRecentlySolvedError();

      return instance(config!);
    } catch (error) {
      return handleFailRefresh();
    }
  }

  return Promise.reject(error);
};

const setupAxiosInterceptors = (redirectToLogout: () => void) => {
  // LEGACY REQUEST
  axiosInstanceLegacy.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    const authToken = getLegacyTokens().authToken;
    request.headers.Authorization = `Bearer ${authToken}`;
    return request;
  });

  // LEGACY RESPONSE
  axiosInstanceLegacy.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      return handleAuthErrors(error, axiosInstanceLegacy, redirectToLogout);
    },
  );

  // NAMEK REQUEST
  axiosInstanceNamek.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    const accessTokenNamek = getNamekToken();
    request.headers.Authorization = `Bearer ${accessTokenNamek}`;
    return request;
  });

  // NAMEK RESPONSE
  axiosInstanceNamek.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error: AxiosError) => {
      return handleAuthErrors(error, axiosInstanceNamek, redirectToLogout);
    },
  );

  // ORION REQUEST
  axiosInstanceOrion.interceptors.request.use((request: InternalAxiosRequestConfig) => {
    if (!isOrionAuthenticateRequest(request.url)) {
      const accessTokenNamek = getNamekToken();
      request.headers.Authorization = `Bearer ${accessTokenNamek}`;
      return request;
    }
    return request;
  });

  // ORION RESPONSE
  axiosInstanceOrion.interceptors.response.use(
    (response) => response,
    async (error) => {
      return handleAuthErrors(error, axiosInstanceOrion, redirectToLogout);
    },
  );
};

export default setupAxiosInterceptors;
